import Layout from '@/components/Layout';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';

import { API } from '../config';
const o = { withCredentials: true };

/* ── Colores por fuente ── */
const FUENTES = {
  'Bot / IA':       { color: '#00C853', bg: '#00C85315' },
  'Trading Manual': { color: '#00B0FF', bg: '#00B0FF15' },
  'Copy Trading':   { color: '#FFD600', bg: '#FFD60015' },
  'Arbitraje':      { color: '#7C3AED', bg: '#7C3AED15' },
};

/* ── Tooltip custom ── */
const TipArea = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1E1E1E] border border-[#27272A] rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-[#A1A1AA] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-bold">
          {p.name}: ${Number(p.value).toFixed(2)}
        </p>
      ))}
    </div>
  );
};

const TipBar = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const t = payload[0].payload;
  return (
    <div className="bg-[#1E1E1E] border border-[#27272A] rounded-lg px-3 py-2 text-xs shadow-xl max-w-[200px]">
      <p className="text-white font-bold truncate">{t.label}</p>
      {t.esComision ? (
        <>
          <p className="text-[#71717A] text-[10px]">Solo comisiones — sin P&amp;L real</p>
          <p className="font-bold text-sm text-[#71717A]">$0.00</p>
        </>
      ) : (
        <>
          <p className="text-[#A1A1AA]">{t.fuente}</p>
          <p className={`font-bold text-sm ${t.pnlReal >= 0 ? 'text-[#00C853]' : 'text-[#FF5252]'}`}>${t.pnlReal?.toFixed(2)}</p>
        </>
      )}
    </div>
  );
};

/* ── Donut estilo Power BI ── */
const DonutPBI = ({ wins, losses, total }) => {
  if (!total) return null;
  const data = [
    { name: 'Ganancias', value: wins,   color: '#00C853' },
    { name: 'Pérdidas',  value: losses, color: '#FF5252' },
  ];
  const pct = total ? Math.round((wins / total) * 100) : 0;
  return (
    <div className="relative flex flex-col items-center">
      <ResponsiveContainer width={180} height={180}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={58} outerRadius={82}
            dataKey="value" startAngle={90} endAngle={-270} strokeWidth={0}>
            {data.map((d, i) => <Cell key={i} fill={d.color} opacity={0.9}/>)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {/* Texto central */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <p className="text-3xl font-black text-white">{pct}%</p>
        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Win Rate</p>
      </div>
      {/* Leyenda */}
      <div className="flex gap-4 mt-1">
        {data.map(d => (
          <span key={d.name} className="flex items-center gap-1 text-xs text-[#A1A1AA]">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: d.color }}/>
            {d.name} ({d.value})
          </span>
        ))}
      </div>
    </div>
  );
};

/* ── Donut por fuente ── */
const DonutFuentes = ({ porFuente }) => {
  const data = Object.entries(porFuente)
    .filter(([, v]) => v.count > 0)
    .map(([k, v]) => ({ name: k, value: v.count, color: FUENTES[k]?.color || '#888' }));
  if (!data.length) return null;
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={160} height={160}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={48} outerRadius={72}
            dataKey="value" startAngle={90} endAngle={-270} strokeWidth={0}>
            {data.map((d, i) => <Cell key={i} fill={d.color} opacity={0.9}/>)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-col gap-1 mt-1 w-full">
        {data.map(d => (
          <span key={d.name} className="flex items-center gap-2 text-[11px] text-[#A1A1AA]">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }}/>
            <span className="flex-1">{d.name}</span>
            <span className="text-white font-bold">{d.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ── Panel principal de gráficos ── */
const PanelGraficos = () => {
  const [trades, setTrades]     = useState([]);
  const [ajustePaper, setAjustePaper] = useState(0);
  const [tabFuente, setTab]     = useState('Todas');

  useEffect(() => {
    // Todas las operaciones del usuario, sin límite de 200 (ver
    // /portfolio/trades-full: esa ventana hacía que el P&L mostrado acá
    // pudiera verse muy negativo aunque el rendimiento real fuera otro,
    // y además etiquetaba mal Copy Trading como Trading Manual).
    axios.get(`${API}/portfolio/trades-full`, o).catch(() => ({ data: { trades: [], ajuste_paper: 0 } })).then(r => {
      setTrades(r.data.trades || []);
      setAjustePaper(r.data.ajuste_paper || 0);
    });
  }, []);

  /* ── Estado vacío ── */
  if (!trades.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center mb-4">
          <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
            <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke="#00C853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p className="text-white font-semibold">Sin operaciones cerradas aún</p>
        <p className="text-[#A1A1AA] text-sm mt-1 max-w-xs">Los gráficos aparecerán aquí cuando se cierren posiciones de bots o manuales.</p>
      </div>
    );
  }

  /* ── Cálculos ── */
  const tabFiltered = tabFuente === 'Todas' ? trades : trades.filter(t => t.fuente === tabFuente);
  const filtradas   = tabFiltered.filter(t => t.pnl !== 0);

  // El ajuste de reseteo de saldo paper solo tiene sentido como punto de
  // partida cuando se ve el TOTAL combinado -- en un tab de una sola
  // fuente (ej. "Arbitraje") mostraría una curva relativa a un ajuste
  // que en realidad es de todo el portafolio, no de esa fuente sola.
  let acc = tabFuente === 'Todas' ? ajustePaper : 0;
  const areaData = filtradas.map((t, i) => ({
    i,
    equity: parseFloat((acc += t.pnl).toFixed(2)),
    fecha:  t.ts.slice(5, 10),
  }));

  const totalPnl = acc;
  const wins     = filtradas.filter(t => t.pnl > 0).length;
  const losses   = filtradas.filter(t => t.pnl < 0).length;
  const best     = filtradas.length ? Math.max(...filtradas.map(t => t.pnl)) : 0;
  const worst    = filtradas.length ? Math.min(...filtradas.map(t => t.pnl)) : 0;

  // Barras incluyen comisiones (|pnl|<0.01) con altura mínima visible y color gris
  const _comisionH = best > 0.1 ? parseFloat((best * 0.04).toFixed(4)) : 0.05;
  const barData = tabFiltered.map(t => {
    const esComision = Math.abs(t.pnl) < 0.01;
    return {
      label:     esComision ? 'COMISIÓN' : t.label,
      pnl:       esComision ? _comisionH : parseFloat(t.pnl.toFixed(2)),
      pnlReal:   t.pnl,
      fuente:    t.fuente,
      esComision,
    };
  });

  /* totales por fuente — cuenta todos los trades cerrados, no solo los >= $0.01 */
  const porFuente = { 'Bot / IA': { count:0, pnl:0 }, 'Trading Manual': { count:0, pnl:0 }, 'Copy Trading': { count:0, pnl:0 }, 'Arbitraje': { count:0, pnl:0 } };
  trades.forEach(t => {
    if (porFuente[t.fuente]) { porFuente[t.fuente].count++; porFuente[t.fuente].pnl += t.pnl; }
  });

  const tabColor = tabFuente === 'Todas' ? '#00C853' : (FUENTES[tabFuente]?.color || '#00C853');

  return (
    <div className="space-y-6">

      {/* ── Fila 1: Stats + Donut Win/Loss + Donut fuentes ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Tarjetas de stats */}
        <div className="grid grid-cols-2 gap-3 content-start">
          {[
            { l:'P&L Total',     v:`$${totalPnl.toFixed(2)}`, c: totalPnl >= 0 ? '#00C853' : '#FF5252' },
            { l:'Operaciones',   v: filtradas.length,         c:'#00B0FF' },
            { l:'Mejor Trade',   v: best  >= 0 ? `+$${best.toFixed(2)}`          : `-$${Math.abs(best).toFixed(2)}`,  c: best  >= 0 ? '#00C853' : '#FF5252' },
            { l:'Peor Trade',    v: worst >= 0 ? `+$${worst.toFixed(2)}`         : `-$${Math.abs(worst).toFixed(2)}`, c: worst >= 0 ? '#00C853' : '#FF5252' },
          ].map(s => (
            <div key={s.l} className="bg-[#1E1E1E] border border-[#27272A] rounded-xl px-4 py-3">
              <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider">{s.l}</p>
              <p className="font-black text-xl font-mono mt-1" style={{ color: s.c }}>{s.v}</p>
            </div>
          ))}
        </div>

        {/* Donut Win/Loss */}
        <div className="bg-[#1E1E1E] border border-[#27272A] rounded-xl p-4 flex flex-col items-center justify-center">
          <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-3 font-semibold">Win / Loss Rate</p>
          <DonutPBI wins={wins} losses={losses} total={wins + losses}/>
        </div>

        {/* Donut por fuente */}
        <div className="bg-[#1E1E1E] border border-[#27272A] rounded-xl p-4 flex flex-col items-center">
          <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-3 font-semibold">Por Fuente</p>
          <DonutFuentes porFuente={porFuente}/>
        </div>
      </div>

      {/* ── Tabs de filtro por fuente ── */}
      <div className="flex gap-2 flex-wrap">
        {['Todas', 'Bot / IA', 'Trading Manual', 'Copy Trading', 'Arbitraje'].map(f => {
          const col = f === 'Todas' ? '#00C853' : FUENTES[f]?.color;
          const active = tabFuente === f;
          return (
            <button key={f} onClick={() => setTab(f)}
              className="px-4 py-1.5 rounded-full text-xs font-semibold border transition-all"
              style={{
                borderColor: active ? col : '#27272A',
                background:  active ? `${col}20` : 'transparent',
                color:       active ? col : '#A1A1AA',
              }}>
              {f}
              {f !== 'Todas' && porFuente[f] && (
                <span className="ml-1.5 opacity-60">({porFuente[f].count})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Área P&L acumulado ── */}
      <div className="bg-[#1E1E1E] border border-[#27272A] rounded-xl p-4">
        <p className="text-white text-sm font-semibold mb-3">
          P&amp;L Acumulado
          <span className="ml-2 text-xs font-normal text-[#A1A1AA]">{tabFuente}</span>
        </p>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={areaData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#00C853" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#00C853" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="gNeg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#FF5252" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#FF5252" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="fecha" tick={{ fill:'#52525B', fontSize:9 }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fill:'#52525B', fontSize:9 }} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
            <Tooltip content={<TipArea/>}/>
            <ReferenceLine y={0} stroke="#27272A" strokeDasharray="4 3"/>
            <Area type="monotone" dataKey="equity" name="P&L"
              stroke={totalPnl >= 0 ? '#00C853' : '#FF5252'} strokeWidth={2.5}
              fill={totalPnl >= 0 ? 'url(#gPos)' : 'url(#gNeg)'}
              dot={false} activeDot={{ r:4, fill: totalPnl >= 0 ? '#00C853' : '#FF5252' }}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Barras por operación ── */}
      <div className="bg-[#1E1E1E] border border-[#27272A] rounded-xl p-4">
        <p className="text-white text-sm font-semibold mb-3">
          P&amp;L por Operación
          <span className="ml-2 text-xs font-normal text-[#A1A1AA]">
            {filtradas.length} operación{filtradas.length !== 1 ? 'es' : ''}
            {barData.length > filtradas.length ? ` · ${barData.length - filtradas.length} comisión` : ''}
          </span>
        </p>
        <ResponsiveContainer width="100%" height={110}>
          <BarChart data={barData} margin={{ top:4, right:8, left:-10, bottom:0 }}>
            <XAxis dataKey="label" tick={false} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fill:'#52525B', fontSize:9 }} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
            <Tooltip content={<TipBar/>}/>
            <ReferenceLine y={0} stroke="#27272A"/>
            <Bar dataKey="pnl" radius={[3,3,0,0]}>
              {barData.map((d, i) => (
                <Cell key={i} fill={d.esComision ? '#52525B' : (d.pnlReal >= 0 ? '#00C853' : '#FF5252')} opacity={0.85}/>
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[#3F3F46] text-[9px] text-center mt-1">Verde = ganancia · Rojo = pérdida · Gris = comisión</p>
      </div>

      {/* ── Resumen por fuente ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(porFuente).map(([fuente, data]) => {
          const col = FUENTES[fuente]?.color || '#888';
          if (!data.count) return null;
          return (
            <div key={fuente} className="bg-[#1E1E1E] border border-[#27272A] rounded-xl p-4"
              style={{ borderLeft: `3px solid ${col}` }}>
              <p className="text-xs font-bold mb-2" style={{ color: col }}>{fuente}</p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[#A1A1AA] text-[10px]">Operaciones</p>
                  <p className="text-white text-xl font-black">{data.count}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#A1A1AA] text-[10px]">P&amp;L</p>
                  <p className="text-lg font-black" style={{ color: data.pnl >= 0 ? '#00C853' : '#FF5252' }}>
                    ${data.pnl.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

/* ── Página IA ── */
const IA = () => (
  <Layout>
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">IA Atenea</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">
          Rendimiento de operaciones por fuente · Abre Atenea desde el botón azul inferior derecho o desde tu perfil
        </p>
      </div>
      <PanelGraficos />
    </div>
  </Layout>
);

export default IA;
